/**
 * 单个角色卡的 JSON 导入 / 导出（T07）。
 * -----------------------------------------------------------------------------
 * - 导出：把一个 `Preset` 序列化成人类可读的 JSON（含 id，导入时原样还原）；
 * - 导入：先逐字段校验 + normalize，**校验全部通过后才由调用方写库**，
 *   文件非法时抛 `PresetImportError`，错误信息带字段路径，便于 UI 明确提示。
 *
 * 导入同时兼容两种形态：
 *   1. 裸对象 `{ id, name, systemPrompt, ... }`（导出的就是这个形态）；
 *   2. 包裹对象 `{ "preset": { ... } }`（手写 / 其他工具产出的文件）。
 * ============================================================================= */

import { newId } from '../../core/ids';
import { parseRoleModelConfig } from '../../core/roleModel';
import type { Preset, WorldBookEntry, WorldBookSelectiveLogic } from '../../core/types';

/** 导出文件的建议扩展名（导入不校验扩展名）。 */
export const PRESET_FILE_EXTENSION = '.json';

const PRESET_SOURCES: readonly NonNullable<Preset['source']>[] = ['local', 'community'];
const SELECTIVE_LOGICS: readonly WorldBookSelectiveLogic[] = ['andAny', 'andAll', 'notAny', 'notAll'];
const AUTOMATION_MODES: readonly NonNullable<Preset['automationMode']>[] = [
  'reply_only',
  'auto_bubble',
  'auto_moment',
  'auto_bubble_moment',
];

/** 导入失败时抛出：message 形如 `worldBook[0].priority：字段类型不符…`。 */
export class PresetImportError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'PresetImportError';
  }
}

function fail(path: string, reason: string): never {
  throw new PresetImportError(`${path}：${reason}`);
}

function typeName(value: unknown): string {
  if (value === null) return 'null';
  if (Array.isArray(value)) return 'array';
  return typeof value;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function requireObject(value: unknown, path: string): Record<string, unknown> {
  if (!isRecord(value)) fail(path, `字段类型不符，期望 object，实际为 ${typeName(value)}`);
  return value;
}

function requireString(source: Record<string, unknown>, field: string, path: string): string {
  const value = source[field];
  if (value === undefined || value === null) fail(`${path}.${field}`, '字段缺失，期望 string');
  if (typeof value !== 'string') {
    fail(`${path}.${field}`, `字段类型不符，期望 string，实际为 ${typeName(value)}`);
  }
  return value;
}

function optionalString(
  source: Record<string, unknown>,
  field: string,
  path: string,
): string | undefined {
  const value = source[field];
  if (value === undefined || value === null) return undefined;
  if (typeof value !== 'string') {
    fail(`${path}.${field}`, `字段类型不符，期望 string，实际为 ${typeName(value)}`);
  }
  return value;
}

function optionalId(source: Record<string, unknown>, field: string, path: string): string {
  const raw = optionalString(source, field, path);
  return raw === undefined || raw.trim() === '' ? newId() : raw;
}

function normalizeStringArray(value: unknown, path: string): string[] {
  if (!Array.isArray(value)) {
    fail(path, `字段类型不符，期望 array，实际为 ${typeName(value)}`);
  }
  return value.map((item, index) => {
    if (typeof item !== 'string') {
      fail(`${path}[${index}]`, `字段类型不符，期望 string，实际为 ${typeName(item)}`);
    }
    return item;
  });
}

export function normalizeWorldBook(value: unknown, path = 'preset.worldBook'): WorldBookEntry[] {
  if (!Array.isArray(value)) {
    fail(path, `字段类型不符，期望 array，实际为 ${typeName(value)}`);
  }
  return value.map((item, index) => {
    const entryPath = `${path}[${index}]`;
    const source = requireObject(item, entryPath);
    const keywords = normalizeStringArray(source.keywords ?? [], `${entryPath}.keywords`);
    const content = requireString(source, 'content', entryPath);
    const enabled = source.enabled;
    if (typeof enabled !== 'boolean') {
      fail(`${entryPath}.enabled`, `字段类型不符，期望 boolean，实际为 ${typeName(enabled)}`);
    }
    const priority = source.priority;
    if (typeof priority !== 'number' || !Number.isFinite(priority)) {
      fail(`${entryPath}.priority`, `字段类型不符，期望 number，实际为 ${typeName(priority)}`);
    }
    const constant = source.constant;
    if (constant !== undefined && typeof constant !== 'boolean') {
      fail(`${entryPath}.constant`, `字段类型不符，期望 boolean，实际为 ${typeName(constant)}`);
    }
    const name = optionalString(source, 'name', entryPath);
    const secondaryKeywords = source.secondaryKeywords === undefined
      ? undefined : normalizeStringArray(source.secondaryKeywords, `${entryPath}.secondaryKeywords`);
    const selectiveLogic = source.selectiveLogic;
    if (selectiveLogic !== undefined && !SELECTIVE_LOGICS.includes(selectiveLogic as WorldBookSelectiveLogic)) {
      fail(`${entryPath}.selectiveLogic`, `取值非法，期望 ${SELECTIVE_LOGICS.join(' / ')}`);
    }
    for (const field of ['caseSensitive', 'matchWholeWords'] as const) {
      if (source[field] !== undefined && typeof source[field] !== 'boolean') {
        fail(`${entryPath}.${field}`, `字段类型不符，期望 boolean，实际为 ${typeName(source[field])}`);
      }
    }
    return {
      id: optionalId(source, 'id', entryPath),
      keywords,
      content,
      enabled,
      priority,
      ...(constant === undefined ? {} : { constant }),
      ...(name === undefined ? {} : { name }),
      ...(secondaryKeywords === undefined ? {} : { secondaryKeywords }),
      ...(selectiveLogic === undefined ? {} : { selectiveLogic: selectiveLogic as WorldBookSelectiveLogic }),
      ...(source.caseSensitive === undefined ? {} : { caseSensitive: source.caseSensitive as boolean }),
      ...(source.matchWholeWords === undefined ? {} : { matchWholeWords: source.matchWholeWords as boolean }),
    };
  });
}

/** 把任意输入规范成一个合法 `Preset`；不合法则抛 `PresetImportError`。 */
export function normalizePreset(input: unknown): Preset {
  const root = requireObject(input, 'preset');
  const wrapped = root.preset;
  const source = wrapped === undefined ? root : requireObject(wrapped, 'preset');

  const name = requireString(source, 'name', 'preset').trim();
  if (name === '') fail('preset.name', '角色卡名称不能为空');

  const preset: Preset = {
    id: optionalId(source, 'id', 'preset'),
    name,
    systemPrompt: requireString(source, 'systemPrompt', 'preset'),
  };
  if (source.modelConfig !== undefined) {
    try { preset.modelConfig = parseRoleModelConfig(source.modelConfig); }
    catch (error) { fail('preset.modelConfig', error instanceof Error ? error.message : '模型配置无效'); }
  }

  const avatar = optionalString(source, 'avatar', 'preset');
  if (avatar !== undefined) preset.avatar = avatar;

  const description = optionalString(source, 'description', 'preset');
  if (description !== undefined) preset.description = description;

  if (source.worldBook !== undefined && source.worldBook !== null) {
    preset.worldBook = normalizeWorldBook(source.worldBook, 'preset.worldBook');
  }
  if (source.tags !== undefined && source.tags !== null) {
    preset.tags = normalizeStringArray(source.tags, 'preset.tags');
  }
  if (source.source !== undefined && source.source !== null) {
    const value = source.source;
    if (typeof value !== 'string' || !PRESET_SOURCES.includes(value as NonNullable<Preset['source']>)) {
      fail('preset.source', `取值非法，期望 ${PRESET_SOURCES.join(' / ')}`);
    }
    preset.source = value as NonNullable<Preset['source']>;
  }
  if (source.automationMode !== undefined && source.automationMode !== null) {
    if (typeof source.automationMode !== 'string' || !AUTOMATION_MODES.includes(source.automationMode as NonNullable<Preset['automationMode']>)) {
      fail('preset.automationMode', '自动化模式取值非法');
    }
    preset.automationMode = source.automationMode as NonNullable<Preset['automationMode']>;
  }
  for (const field of ['autoImageEnabled', 'autoTtsEnabled', 'ttsAutoVoiceDesign'] as const) {
    const value = source[field];
    if (value === undefined || value === null) continue;
    if (typeof value !== 'boolean') fail(`preset.${field}`, '字段类型不符，期望 boolean');
    preset[field] = value;
  }
  if (source.imageGenerationPolicy !== undefined && source.imageGenerationPolicy !== null) {
    const value = source.imageGenerationPolicy;
    if (value !== 'user_priority' && value !== 'character_priority' && value !== 'balanced') {
      fail('preset.imageGenerationPolicy', '生图积极性策略取值非法');
    }
    preset.imageGenerationPolicy = value;
  }
  if (source.ttsGenerationPolicy !== undefined && source.ttsGenerationPolicy !== null) {
    const value = source.ttsGenerationPolicy;
    if (value !== 'user_priority' && value !== 'character_priority' && value !== 'balanced') {
      fail('preset.ttsGenerationPolicy', '语音 TTS 积极性策略取值非法');
    }
    preset.ttsGenerationPolicy = value;
  }
  for (const field of ['imageProviderId', 'imageModel', 'ttsProviderId', 'ttsModel'] as const) {
    const value = optionalString(source, field, 'preset');
    if (value !== undefined && value.trim() !== '') preset[field] = value;
  }
  if (source.ttsVoiceMode !== undefined && source.ttsVoiceMode !== null) {
    if (source.ttsVoiceMode !== 'preset' && source.ttsVoiceMode !== 'design' && source.ttsVoiceMode !== 'clone') fail('preset.ttsVoiceMode', '语音模式取值非法');
    preset.ttsVoiceMode = source.ttsVoiceMode;
  }
  if (source.quietPeriods !== undefined && source.quietPeriods !== null) {
    if (!Array.isArray(source.quietPeriods)) fail('preset.quietPeriods', '字段类型不符，期望 array');
    preset.quietPeriods = source.quietPeriods.map((item, index) => {
      const period = requireObject(item, `preset.quietPeriods[${index}]`);
      const start = optionalString(period, 'start', `preset.quietPeriods[${index}]`);
      const end = optionalString(period, 'end', `preset.quietPeriods[${index}]`);
      if (!start || !end) fail(`preset.quietPeriods[${index}]`, 'start/end 不能为空');
      return { id: optionalId(period, 'id', `preset.quietPeriods[${index}]`), start, end };
    });
  }

  return preset;
}

/** 解析 JSON 文本并校验（不写库）。 */
export function parsePresetJson(text: string): Preset {
  let parsed: unknown;
  try {
    parsed = JSON.parse(text);
  } catch {
    throw new PresetImportError('导入失败：文件不是合法 JSON');
  }
  return normalizePreset(parsed);
}

/** 序列化单个角色卡为 JSON 文本（默认 2 空格缩进，便于阅读与手工修改）。 */
export function serializePreset(preset: Preset, space = 2): string {
  return JSON.stringify({ ...preset, ...(preset.modelConfig === undefined ? {} : { modelConfig: parseRoleModelConfig(preset.modelConfig) }) }, null, space);
}

/** 由角色卡名称生成安全的导出文件名（去掉路径分隔符等非法字符）。 */
export function presetFileName(preset: Preset): string {
  const base = preset.name.trim().replace(/[\\/:*?"<>|]/g, '_') || 'preset';
  return `${base}${PRESET_FILE_EXTENSION}`;
}
